import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

const SNAP_COOLDOWN = 700; // 마지막 휠 이후 이만큼 조용하면 해제
const MAX_LOCK = 1400; // 스냅 시작 후 최대 잠금 시간 (연장 상한)
const SWIPE_THRESHOLD = 24;
const AUTO_STEP_INTERVAL = 2000;

/**
 * 자동화 수준 섹션 — 스크롤 위치에 따른 단계 전환.
 *
 * 두 가지 모드로 동작하고, 쓰지 않는 쪽은 아예 붙이지 않는다.
 * 특히 자동 재생 기기에 비패시브 touchmove 리스너가 남아 있으면
 * 브라우저 스크롤 최적화가 제한된다.
 *
 * @param {{current: HTMLElement|null}} sectionRef `.automation-gradient` 섹션
 * @param {number} stepCount `.auto-step` 개수
 * @returns {{activeIndex: number, armed: boolean}}
 *   `activeIndex` 는 보여줄 단계, `armed` 는 구동부가 실제로 단계를 잡은 뒤에만 true.
 *   첫 단계는 처음부터 화면에 있어야 하지만 숫자 카운트업은 구동부가 켤 때 돌아야 해서
 *   (모바일 자동 재생은 섹션이 보일 때까지 0 을 유지한다) 두 값을 나눠 돌려준다.
 */
export function useAutomationSteps(sectionRef, stepCount) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [step, setStepState] = useState({ index: 0, armed: false });

  // 스크롤 핸들러가 매번 새 클로저를 잡지 않도록 최신값을 ref 로 읽는다
  const activeIndexRef = useRef(0);
  const armedRef = useRef(false);
  const setStep = useCallback((index) => {
    if (armedRef.current && activeIndexRef.current === index) return;
    activeIndexRef.current = index;
    armedRef.current = true;
    setStepState({ index, armed: true });
  }, []);

  /* ===============================================================
   * 모드 판정
   *
   * 조건 정본은 CSS 의 --auto-play 플래그다 (styles/mobile.css).
   * 같은 미디어쿼리를 JS 에도 적어두면 한쪽만 고쳤을 때 "섹션 높이는
   * 100vh 인데 스냅이 도는" 식으로 어긋난다.
   *
   * 터치 기기는 스크롤에 단계를 묶지 않는다 — 손가락을 뗀 뒤의 관성
   * 스크롤은 touchmove 를 만들지 않아 가로챌 수가 없고, 그래서 플링
   * 한 번에 30 → 90 으로 건너뛰었다
   * ============================================================= */
  const [mode, setMode] = useState(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    /* --auto-play 는 CSS 가 선언한 기본값(0)이 항상 있으므로, 빈 문자열이 나오면
       "아직 스타일이 안 붙었다"는 뜻이다 — Vite dev 서버는 CSS 를 JS 로 주입해서
       첫 이펙트 시점엔 비어 있을 수 있다. 그때 'snap' 으로 단정하면 모바일에서도
       스냅 모드로 굳어 자동 재생이 영영 돌지 않는다. 그래서 값이 잡힐 때까지 기다린다 */
    const MAX_WAIT_FRAMES = 60; // 약 1초. 그 뒤로는 CSS 기본값과 같은 snap 으로 둔다
    let waited = 0;
    let frame = 0;

    const sync = () => {
      const flag = getComputedStyle(section).getPropertyValue('--auto-play').trim();
      if (!flag && waited < MAX_WAIT_FRAMES) {
        waited += 1;
        frame = requestAnimationFrame(sync);
        return;
      }
      setMode(flag === '1' ? 'auto' : 'snap');
    };

    sync();

    // 리사이즈·회전으로 경계를 넘나들 때만 재구성한다
    // (포인터 종류는 세션 중 거의 바뀌지 않는다)
    const onResize = () => {
      waited = 0;
      cancelAnimationFrame(frame);
      sync();
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [sectionRef]);

  /* ===============================================================
   * 스크롤 연동 + 단계 스냅 (마우스·트랙패드)
   *
   * 섹션에 들어오는 스크롤부터 가로채 30% 에 세우고, 이후 스크롤 한 번에
   * 70% → 90% → 다음 섹션으로 한 단계씩만 이동한다. 가로채지 않으면 위
   * 섹션에서 내려오는 관성 스크롤이 섹션 안쪽까지 밀고 들어와 첫 단계를
   * 볼 수 없다. 역방향도 대칭 — 경계(endY)에 한 번 세우고 한 번 더 굴려야
   * 섹션 안으로 들어온다
   * ============================================================= */
  useEffect(() => {
    const automation = sectionRef.current;
    if (mode !== 'snap' || !automation || !stepCount) return undefined;

    const controller = new AbortController();
    const { signal } = controller;

    /* 무대 좌표는 스크롤/리사이즈 때 한 번만 재는 값이다.
       휠 이벤트마다 재면(트랙패드는 초당 60~100 회) getBoundingClientRect ·
       offsetHeight 가 매번 레이아웃을 강제 계산한다 */
    const header = document.querySelector('.site-header');
    const metrics = { top: 0, gap: 0, height: 0, headerHeight: 0, viewport: 0 };
    let ticking = false;

    const measure = () => {
      metrics.top = automation.getBoundingClientRect().top + window.scrollY;
      metrics.height = automation.offsetHeight;
      metrics.headerHeight = header ? header.offsetHeight : 0;
      metrics.viewport = window.innerHeight;
      const scrollable = metrics.height - metrics.viewport;
      metrics.gap = scrollable > 0 ? scrollable / stepCount : 0;
    };

    // 전환 경계보다 1px 안쪽에 세워 해당 단계가 확실히 활성화되게 한다
    const stepY = (index) =>
      Math.round(metrics.top + metrics.gap * index) + (index > 0 ? 1 : 0);

    // 섹션을 완전히 벗어난 위치 (다음 섹션 상단이 헤더 바로 아래)
    const exitY = () =>
      Math.round(metrics.top + metrics.height - metrics.headerHeight);

    const update = () => {
      ticking = false;
      measure();

      // sticky 뷰포트가 붙어 있는 구간 내에서의 진행률 (0~1)
      if (metrics.gap <= 0) {
        setStep(stepCount - 1);
        return;
      }

      const scrollable = metrics.gap * stepCount;
      const progress = Math.min(
        Math.max((window.scrollY - metrics.top) / scrollable, 0),
        1,
      );
      // 경계에 정확히 착지하면 부동소수 오차(0.333…×3 = 0.999…)로 이전 단계에 머문다.
      // 스냅이 전환 지점에 딱 맞춰 멈추므로 미세 보정이 필요하다
      const index = Math.min(
        Math.floor(progress * stepCount + 1e-6),
        stepCount - 1,
      );
      setStep(index);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { signal });
    update();

    let snapLocked = false;
    let unlockTimer = 0;
    let lockStart = 0;

    const unlock = () => {
      window.clearTimeout(unlockTimer);
      unlockTimer = 0;
      snapLocked = false;
    };

    // 디바운스로 해제하되 MAX_LOCK 을 넘겨 연장하지 않는다.
    // 상한이 없으면 휠을 계속 굴리는 동안 "마지막 이벤트 후 정적" 조건이
    // 영영 성립하지 않아 페이지가 잠긴 채 멈춘다
    const scheduleUnlock = () => {
      const remaining = MAX_LOCK - (Date.now() - lockStart);
      if (remaining <= 0) {
        unlock();
        return;
      }
      window.clearTimeout(unlockTimer);
      unlockTimer = window.setTimeout(unlock, Math.min(SNAP_COOLDOWN, remaining));
    };

    const snapToY = (y) => {
      snapLocked = true;
      lockStart = Date.now();
      scheduleUnlock();
      window.scrollTo({ top: y, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    };

    const handleSnap = (event, direction) => {
      if (metrics.gap <= 0) return;

      const y = window.scrollY;
      const { top } = metrics;
      const last = stepCount - 1;
      const lastY = stepY(last); // 마지막 단계(90%)가 서는 위치
      const endY = exitY(); // 섹션을 완전히 벗어난 위치
      const reach = metrics.viewport * 0.9; // 섹션이 화면에 들어오기 시작하는 거리
      const returnBand = metrics.viewport * 0.3; // 아래에서 위로 되돌아올 때 걸리는 거리

      if (snapLocked) {
        if (Date.now() - lockStart >= MAX_LOCK) {
          // 상한 도달 — 더는 붙잡지 않고 이번 이벤트부터 정상 처리한다
          unlock();
        } else {
          // 관성 스크롤의 잔여 이벤트 — 흡수하고 (상한 안에서) 잠금을 연장한다
          if (y >= top - reach && y <= endY + reach) {
            event.preventDefault();
            scheduleUnlock();
          }
          return;
        }
      }

      if (direction > 0) {
        // ── 아래로 ────────────────────────────────────────────────
        if (y < top - 2) {
          // 위 섹션에서 내려오는 중. 섹션이 화면에 들어올 거리면 첫 단계에 세운다
          if (top - y > reach) return;
          event.preventDefault();
          snapToY(stepY(0));
        } else if (y < lastY - 2) {
          // 단계 사이 — 다음 단계로
          event.preventDefault();
          snapToY(stepY(Math.min(activeIndexRef.current + 1, last)));
        } else if (y < endY - 2) {
          // 마지막 단계에서 한 번 더 → 섹션 밖으로
          event.preventDefault();
          snapToY(endY);
        }
        // 섹션을 벗어난 뒤로는 자유 스크롤
        return;
      }

      // ── 위로 ──────────────────────────────────────────────────
      if (y > endY + 2) {
        /* 아래 섹션(기능 카드)을 보는 중.
           경계에서 멀면 가로채지 않는다 — 예전에는 reach(0.9 뷰포트) 안이면
           바로 마지막 단계로 끌어올려, 아래 섹션을 읽다가 조금만 위로
           굴려도 자동화 섹션으로 말려 올라갔다.
           경계 근처에서는 "섹션 밖(endY)"에 한 번 세우고, 거기서 한 번 더
           굴려야 자동화 안으로 들어간다 (아래로 갈 때와 대칭) */
        if (y - endY > returnBand) return;
        event.preventDefault();
        snapToY(endY);
      } else if (y > lastY + 2) {
        // 경계에 서 있다가 한 번 더 — 마지막 단계로 진입
        event.preventDefault();
        snapToY(lastY);
      } else if (y > top + 2) {
        // 단계 사이 — 이전 단계로
        event.preventDefault();
        snapToY(stepY(Math.max(activeIndexRef.current - 1, 0)));
      }
      // 첫 단계에서 위로는 가로채지 않는다 (섹션 위로 자유롭게 벗어남)
    };

    if (!prefersReducedMotion) {
      window.addEventListener(
        'wheel',
        (event) => {
          if (event.ctrlKey) return; // 확대/축소 제스처
          if (Math.abs(event.deltaY) < 2) return;
          handleSnap(event, event.deltaY > 0 ? 1 : -1);
        },
        { passive: false, signal },
      );

      let touchStartY = null;

      window.addEventListener(
        'touchstart',
        (event) => {
          touchStartY = event.touches.length === 1 ? event.touches[0].clientY : null;
        },
        { passive: true, signal },
      );

      window.addEventListener(
        'touchmove',
        (event) => {
          if (touchStartY === null) return;
          const delta = touchStartY - event.touches[0].clientY;
          if (Math.abs(delta) < SWIPE_THRESHOLD) return;
          touchStartY = null;
          handleSnap(event, delta > 0 ? 1 : -1);
        },
        { passive: false, signal },
      );

      window.addEventListener(
        'touchend',
        () => {
          touchStartY = null;
        },
        { passive: true, signal },
      );
    }

    return () => {
      controller.abort();
      unlock();
    };
  }, [mode, sectionRef, stepCount, prefersReducedMotion, setStep]);

  /* ===============================================================
   * 자동 재생 (터치 기기·좁은 화면)
   *
   * 섹션이 화면 절반 이상 보이면 2초 간격으로 30 → 70 → 90 을 재생하고
   * 멈춘다. 방문당 한 번만 재생하고 그 뒤로는 90% 를 유지한다 —
   * 재진입마다 되감으면 오르내릴 때마다 숫자가 다시 세어져 산만하다.
   * 재생 중 화면을 벗어나도 타이머는 그대로 돌아 90% 까지 마친다
   * ============================================================= */
  const autoPlayedRef = useRef(false);

  useEffect(() => {
    const automation = sectionRef.current;
    if (mode !== 'auto' || !automation || !stepCount) return undefined;

    let autoTimer = 0;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || autoPlayedRef.current) return;
          autoPlayedRef.current = true;
          obs.disconnect();

          // 모션 최소화 설정이면 애니메이션 없이 결론(마지막 단계)만 보여준다
          if (prefersReducedMotion) {
            setStep(stepCount - 1);
            return;
          }

          setStep(0);

          const advance = () => {
            if (activeIndexRef.current >= stepCount - 1) return; // 마지막에서 멈춤
            setStep(activeIndexRef.current + 1);
            autoTimer = window.setTimeout(advance, AUTO_STEP_INTERVAL);
          };

          autoTimer = window.setTimeout(advance, AUTO_STEP_INTERVAL);
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(automation);

    return () => {
      observer.disconnect();
      window.clearTimeout(autoTimer);
    };
  }, [mode, sectionRef, stepCount, prefersReducedMotion, setStep]);

  return step;
}
